/**
 * PESO Compliance Dialog Component
 * Dialog for marking batches as PESO compliant with certificate upload
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-004
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { usePesoCompliance } from '@/hooks/inventory/usePesoCompliance';
import type { BatchResponse, PesoComplianceResponse } from '@/types/inventory';
import { isValidPesoCertificateNumber } from '@/types/inventory';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Calendar as CalendarIcon,
  Shield,
  Upload,
  FileText,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// FORM VALIDATION SCHEMA
// ============================================

const pesoComplianceSchema = z
  .object({
    certificateNumber: z
      .string()
      .min(1, 'Certificate number is required')
      .refine(isValidPesoCertificateNumber, {
        message: 'Invalid format. Use PESO-YYYY-XXXXXX (e.g., PESO-2024-001234)',
      }),
    issueDate: z.date({
      required_error: 'Issue date is required',
    }).refine((date) => date <= new Date(), {
      message: 'Issue date cannot be in the future',
    }),
    expiryDate: z.date({
      required_error: 'Expiry date is required',
    }),
    inspectorName: z
      .string()
      .min(1, 'Inspector name is required')
      .min(3, 'Inspector name must be at least 3 characters')
      .max(100, 'Inspector name cannot exceed 100 characters'),
    remarks: z
      .string()
      .max(500, 'Remarks cannot exceed 500 characters')
      .optional(),
    certificateFile: z
      .instanceof(File, { message: 'Certificate file is required' })
      .refine((file) => file.size > 0, 'Certificate file is required')
      .refine(
        (file) => file.size <= 5 * 1024 * 1024,
        'File size must not exceed 5MB'
      )
      .refine(
        (file) => {
          const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
          return allowedTypes.includes(file.type);
        },
        'Only PDF, JPEG, and PNG files are allowed'
      ),
  })
  .refine((data) => data.expiryDate > data.issueDate, {
    message: 'Expiry date must be after issue date',
    path: ['expiryDate'],
  });

type PesoComplianceFormValues = z.infer<typeof pesoComplianceSchema>;

// ============================================
// COMPONENT PROPS
// ============================================

interface PesoComplianceDialogProps {
  batch: BatchResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (response: PesoComplianceResponse) => void;
}

// ============================================
// COMPONENT
// ============================================

export function PesoComplianceDialog({
  batch,
  open,
  onOpenChange,
  onSuccess,
}: PesoComplianceDialogProps) {
  const [issueDateCalendarOpen, setIssueDateCalendarOpen] = useState(false);
  const [expiryDateCalendarOpen, setExpiryDateCalendarOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { isMarking, error, markPesoCompliant, reset, validateCertificateFile } =
    usePesoCompliance({
      onSuccess: (response) => {
        toast.success('PESO Compliant Marked', {
          description: `Batch ${batch.batchNumber} marked as PESO compliant with certificate ${response.certificateNumber}`,
        });
        onOpenChange(false);
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (error) => {
        toast.error('Marking Failed', {
          description: error.message,
        });
      },
    });

  const form = useForm<PesoComplianceFormValues>({
    resolver: zodResolver(pesoComplianceSchema),
    defaultValues: {
      certificateNumber: '',
      issueDate: undefined,
      expiryDate: undefined,
      inspectorName: '',
      remarks: '',
      certificateFile: undefined,
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        certificateNumber: '',
        issueDate: undefined,
        expiryDate: undefined,
        inspectorName: '',
        remarks: '',
        certificateFile: undefined,
      });
      setSelectedFile(null);
      reset();
    }
  }, [open, form, reset]);

  // Handle file selection
  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateCertificateFile(file);
      if (validation.valid) {
        setSelectedFile(file);
        form.setValue('certificateFile', file, { shouldValidate: true });
        form.clearErrors('certificateFile');
      } else {
        setSelectedFile(null);
        form.setError('certificateFile', {
          type: 'manual',
          message: validation.error,
        });
      }
    }
  }

  // Handle form submission
  async function onSubmit(values: PesoComplianceFormValues) {
    const requestData = {
      certificateNumber: values.certificateNumber,
      issueDate: format(values.issueDate, 'yyyy-MM-dd'),
      expiryDate: format(values.expiryDate, 'yyyy-MM-dd'),
      inspectorName: values.inspectorName,
      remarks: values.remarks || undefined,
    };

    await markPesoCompliant(batch.id, requestData, values.certificateFile);
  }

  // Check if batch is allocated
  const isAllocated = batch.allocatedQuantity > 0;

  // Check if already PESO compliant
  const isAlreadyPesoCompliant = batch.pesoCompliant && batch.pesoCertificate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="peso-dialog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Mark PESO Compliant
          </DialogTitle>
          <DialogDescription id="peso-dialog-description">
            Mark batch {batch.batchNumber} as PESO compliant with certificate upload
          </DialogDescription>
        </DialogHeader>

        {/* Allocation Warning */}
        {isAllocated && (
          <Alert className="border-red-300 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>Warning:</strong> Cannot mark batch as PESO compliant after
              allocation. This batch has {batch.allocatedQuantity}{' '}
              {batch.quantityUnit} allocated. Only unallocated batches can be
              marked PESO compliant.
            </AlertDescription>
          </Alert>
        )}

        {/* Already PESO Compliant Info */}
        {isAlreadyPesoCompliant && (
          <Alert className="border-blue-300 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              This batch is already PESO compliant with certificate{' '}
              <strong>{batch.pesoCertificate?.number}</strong> (expires on{' '}
              {batch.pesoCertificate?.expiryDate &&
                format(new Date(batch.pesoCertificate.expiryDate), 'PPP')}
              ). You can update the certificate by submitting a new one.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Batch Information */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Label className="text-sm text-gray-600 mb-2 block">
                Batch Information
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Batch Number</div>
                  <div className="font-semibold">{batch.batchNumber}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Product Type</div>
                  <Badge variant="outline">{batch.productType}</Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Quality Grade</div>
                  <Badge>Grade {batch.qualityGrade}</Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Available Quantity</div>
                  <div className="font-semibold">
                    {batch.availableQuantity} {batch.quantityUnit}
                  </div>
                </div>
              </div>
            </div>

            {/* Certificate Number */}
            <FormField
              control={form.control}
              name="certificateNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Certificate Number *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="PESO-2024-001234"
                      disabled={isMarking || isAllocated}
                      {...field}
                      aria-label="Certificate Number"
                    />
                  </FormControl>
                  <FormDescription>
                    Format: PESO-YYYY-XXXXXX (e.g., PESO-2024-001234)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Issue Date & Expiry Date */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="issueDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Issue Date *</FormLabel>
                    <Popover
                      open={issueDateCalendarOpen}
                      onOpenChange={setIssueDateCalendarOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                            disabled={isMarking || isAllocated}
                            aria-label="Select Issue Date"
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setIssueDateCalendarOpen(false);
                          }}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>Cannot be in future</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expiry Date *</FormLabel>
                    <Popover
                      open={expiryDateCalendarOpen}
                      onOpenChange={setExpiryDateCalendarOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                            disabled={isMarking || isAllocated}
                            aria-label="Select Expiry Date"
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setExpiryDateCalendarOpen(false);
                          }}
                          disabled={(date) => {
                            const issueDate = form.getValues('issueDate');
                            return issueDate ? date <= issueDate : false;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>Must be after issue date</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Inspector Name */}
            <FormField
              control={form.control}
              name="inspectorName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inspector Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter PESO inspector's full name"
                      disabled={isMarking || isAllocated}
                      {...field}
                      aria-label="Inspector Name"
                    />
                  </FormControl>
                  <FormDescription>
                    Full name of the PESO inspector who issued the certificate
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Certificate File Upload */}
            <FormField
              control={form.control}
              name="certificateFile"
              render={({ field: { onChange, value, ...field } }) => (
                <FormItem>
                  <FormLabel>Certificate File *</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          disabled={isMarking || isAllocated}
                          onChange={handleFileSelect}
                          className="hidden"
                          id="certificate-file-input"
                          aria-label="Upload Certificate File"
                          {...field}
                        />
                        <label
                          htmlFor="certificate-file-input"
                          className={cn(
                            'flex items-center justify-center gap-2 px-4 py-2 border rounded-md cursor-pointer transition-colors',
                            isMarking || isAllocated
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'hover:bg-gray-50 border-gray-300'
                          )}
                        >
                          <Upload className="w-4 h-4" />
                          Choose File
                        </label>
                        {selectedFile && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md flex-1">
                            <FileText className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-800 flex-1 truncate">
                              {selectedFile.name}
                            </span>
                            <span className="text-xs text-green-600">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedFile(null);
                                form.setValue('certificateFile', undefined as any);
                              }}
                              className="text-green-600 hover:text-green-800"
                              disabled={isMarking || isAllocated}
                              aria-label="Remove File"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      {!selectedFile && (
                        <p className="text-sm text-gray-500">
                          PDF, JPEG, or PNG (max 5MB)
                        </p>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remarks */}
            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes or observations about the PESO certification..."
                      className="min-h-[80px] resize-none"
                      disabled={isMarking || isAllocated}
                      {...field}
                      aria-label="Remarks"
                    />
                  </FormControl>
                  <FormDescription>
                    {field.value?.length || 0}/500 characters (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error Display */}
            {error && (
              <Alert className="border-red-300 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {/* Success Indicator */}
            {form.formState.isValid && selectedFile && !isMarking && (
              <Alert className="border-green-300 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  All fields are valid. Ready to mark PESO compliant.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isMarking}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isMarking || isAllocated || !form.formState.isValid}
              >
                {isMarking ? 'Marking...' : 'Mark PESO Compliant'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
